import * as yup from 'yup';

import { TIN_PATTERN } from '@/constants/patterns';

import { getTranslatedMessage } from '@/i18n/utils';

import {
  TaxNumberTypeCode,
  UsTaxNoReturnReasonCode,
} from '@/types/api/tax-info';

import { USTaxReturnFormValues } from '../utils/types';
import {
  US_TAX_NO_RETURN_REASON_CODES,
  US_TAX_NO_RETURN_REASON_OTHER_MAX_LENGTH,
} from './constants';
import { getUsTaxNumberMaxLength, getUsTaxNumberMinLength } from './utils';

const US_TAX_NUMBER_TYPE_CODES = [
  TaxNumberTypeCode.SSN,
  TaxNumberTypeCode.ITIN,
] as const;

type UsTaxReturnValidationOptions = {
  hasUsIndicia: boolean;
};

export function isUsTaxReturnFormValid(
  values: USTaxReturnFormValues,
  options: UsTaxReturnValidationOptions,
): boolean {
  return getUsTaxReturnValidationSchema(options).isValidSync(values);
}

export function getUsTaxReturnValidationSchema({
  hasUsIndicia,
}: UsTaxReturnValidationOptions) {
  return yup.object({
    usTaxReturnRequired: yup
      .boolean()
      .required(getTranslatedMessage('taxInfoDrawer.question1.required')),
    taxNumberTypeCode: yup
      .mixed<TaxNumberTypeCode>()
      .when('usTaxReturnRequired', {
        is: true,
        then: schema =>
          schema
            .oneOf([...US_TAX_NUMBER_TYPE_CODES])
            .required(
              getTranslatedMessage(
                'taxInfoDrawer.question1.usTaxType.required',
              ),
            ),
        otherwise: schema => schema.notRequired(),
      }),
    usTaxNumber: yup
      .string()
      .when(['usTaxReturnRequired', 'taxNumberTypeCode'], {
        is: (
          usTaxReturnRequired: boolean,
          taxNumberTypeCode?: TaxNumberTypeCode,
        ) => usTaxReturnRequired === true && !!taxNumberTypeCode,
        then: schema =>
          schema
            .required(
              getTranslatedMessage(
                'taxInfoDrawer.question1.usTaxNumber.required',
              ),
            )
            .matches(
              new RegExp(TIN_PATTERN),
              getTranslatedMessage(
                'taxInfoDrawer.question1.usTaxNumber.error.pattern',
              ),
            )
            .test('us-tax-number-length', function validateUsTaxNumber(value) {
              const taxNumberTypeCode = this.parent.taxNumberTypeCode as
                | TaxNumberTypeCode
                | undefined;

              if (!value || !taxNumberTypeCode) {
                return true;
              }

              const minLength = getUsTaxNumberMinLength(taxNumberTypeCode);
              const maxLength = getUsTaxNumberMaxLength(taxNumberTypeCode);

              if (value.length < minLength) {
                return this.createError({
                  message: getTranslatedMessage(
                    'taxInfoDrawer.question1.usTaxNumber.error.minimum',
                    { value: minLength },
                  ),
                });
              }

              if (value.length > maxLength) {
                return this.createError({
                  message: getTranslatedMessage(
                    'taxInfoDrawer.question1.usTaxNumber.error.maximum',
                    { value: maxLength },
                  ),
                });
              }

              return true;
            }),
        otherwise: schema => schema.notRequired(),
      }),
    usTaxNoReturnReasonCode: yup
      .mixed<UsTaxNoReturnReasonCode>()
      .when('usTaxReturnRequired', {
        is: (usTaxReturnRequired: boolean) =>
          usTaxReturnRequired === false && hasUsIndicia,
        then: schema =>
          schema
            .oneOf([...US_TAX_NO_RETURN_REASON_CODES])
            .required(
              getTranslatedMessage(
                'taxInfoDrawer.question1.noReturnReason.required',
              ),
            ),
        otherwise: schema => schema.notRequired(),
      }),
    usTaxNoReturnReasonOther: yup
      .string()
      .when(['usTaxReturnRequired', 'usTaxNoReturnReasonCode'], {
        is: (
          usTaxReturnRequired: boolean,
          usTaxNoReturnReasonCode?: UsTaxNoReturnReasonCode,
        ) =>
          usTaxReturnRequired === false &&
          hasUsIndicia &&
          usTaxNoReturnReasonCode === UsTaxNoReturnReasonCode.OTHER,
        then: schema =>
          schema
            .required(
              getTranslatedMessage(
                'taxInfoDrawer.question1.noReturnReasonOther.required',
              ),
            )
            .max(
              US_TAX_NO_RETURN_REASON_OTHER_MAX_LENGTH,
              getTranslatedMessage(
                'taxInfoDrawer.question1.noReturnReasonOther.maxLength',
                { count: US_TAX_NO_RETURN_REASON_OTHER_MAX_LENGTH },
              ),
            ),
        otherwise: schema => schema.notRequired(),
      }),
  });
}
